import { useCallback, useMemo } from "react";
import {
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import { IconSearch } from "@tabler/icons-react";
import {
  getLabTodayDateOnly,
  type DateOnlyString,
} from "../../../utils/dateOnly";

export type CommunicationLogDateRange = [
  DateOnlyString | null,
  DateOnlyString | null
];

export interface CommunicationLogFilterValues {
  channel: string;
  status: string;
  routingDecision: string;
  communicationType: string;
  recipient: string;
  caseNumber: string;
  dateRange: CommunicationLogDateRange;
}

export interface CommunicationLogFilterActions {
  setChannel: (value: string) => void;
  setStatus: (value: string) => void;
  setRoutingDecision: (value: string) => void;
  setCommunicationType: (value: string) => void;
  setRecipient: (value: string) => void;
  setCaseNumber: (value: string) => void;
  setDateRange: (value: DatesRangeValue) => void;
}

interface CommunicationLogFiltersPanelProps {
  values: CommunicationLogFilterValues;
  actions: CommunicationLogFilterActions;
  hasActiveFilters: boolean;
  onClear: () => void;
}

const CHANNEL_OPTIONS = [
  { value: "", label: "All Channels" },
  { value: "email", label: "Email" },
  { value: "hl7", label: "HL7" },
  { value: "fax", label: "Fax" },
  { value: "sms", label: "SMS" },
  { value: "voice", label: "Voice" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "bounced", label: "Bounced" },
];

const ROUTING_OPTIONS = [
  { value: "", label: "All routing" },
  { value: "redirected", label: "Redirected" },
  { value: "bypassed", label: "Live — prefix bypass" },
  { value: "direct", label: "Direct" },
  { value: "blocked", label: "Blocked" },
  { value: "unknown", label: "Unknown" },
];

const LEGACY_COMMUNICATION_TYPES: Record<string, string[]> = {
  staff_invitation_account_created: ["invitation"],
  staff_password_reset: ["password-reset"],
  staff_welcome: ["welcome"],
  macro_request_submitted: ["request-submitted"],
  macro_request_approved: ["macro-request-approved"],
  macro_request_rejected: ["macro-request-rejected"],
  report_delivery: ["report_delivery"],
  observation_new_alert: ["new-observation-alert"],
  observation_digest: ["observation-digest"],
  portal_invitation_report_ready: ["report_portal_link"],
  portal_invitation_first_time: [
    "physician-portal-invitation",
    "portal_invitation",
  ],
  portal_access_added: ["physician-portal-added", "portal_access_added"],
  portal_report_notification_new: [
    "physician-portal-notification",
    "portal_report_notification",
  ],
  portal_otp: ["physician-portal-otp", "portal_otp"],
  portal_supply_request: ["portal-supply-request", "portal_supply_request"],
};

function getCommunicationTypeDescription(
  communicationType: string
): string | undefined {
  if (!communicationType) return undefined;
  const canonicalType = Object.entries(LEGACY_COMMUNICATION_TYPES).find(
    ([type, legacyTypes]) =>
      type === communicationType || legacyTypes.includes(communicationType)
  );
  if (!canonicalType) return undefined;
  return `Includes historical types: ${canonicalType[1].join(", ")}`;
}

export default function CommunicationLogFiltersPanel({
  values,
  actions,
  hasActiveFilters,
  onClear,
}: CommunicationLogFiltersPanelProps) {
  const maximumDate = useMemo(() => getLabTodayDateOnly(), []);
  const handleChannelChange = useCallback(
    (value: string | null) => actions.setChannel(value || ""),
    [actions]
  );
  const handleStatusChange = useCallback(
    (value: string | null) => actions.setStatus(value || ""),
    [actions]
  );
  const handleRoutingChange = useCallback(
    (value: string | null) => actions.setRoutingDecision(value || ""),
    [actions]
  );
  const handleTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      actions.setCommunicationType(event.currentTarget.value),
    [actions]
  );
  const handleRecipientChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      actions.setRecipient(event.currentTarget.value),
    [actions]
  );
  const handleCaseNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      actions.setCaseNumber(event.currentTarget.value),
    [actions]
  );

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group grow align="end">
          <Select
            label="Channel"
            placeholder="All Channels"
            data={CHANNEL_OPTIONS}
            value={values.channel}
            onChange={handleChannelChange}
            clearable
          />
          <Select
            label="Status"
            placeholder="All Statuses"
            data={STATUS_OPTIONS}
            value={values.status}
            onChange={handleStatusChange}
            clearable
          />
          <Select
            label="Routing"
            placeholder="All routing"
            data={ROUTING_OPTIONS}
            value={values.routingDecision}
            onChange={handleRoutingChange}
            clearable
          />
          <TextInput
            label="Type"
            placeholder="e.g. portal_invitation"
            description={getCommunicationTypeDescription(
              values.communicationType
            )}
            value={values.communicationType}
            onChange={handleTypeChange}
          />
          <TextInput
            label="Intended recipient"
            placeholder="Email, phone, fax"
            leftSection={<IconSearch size={14} />}
            value={values.recipient}
            onChange={handleRecipientChange}
          />
          <TextInput
            label="Case #"
            placeholder="Case number"
            value={values.caseNumber}
            onChange={handleCaseNumberChange}
          />
          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Select date range"
            value={values.dateRange}
            onChange={actions.setDateRange}
            maxDate={maximumDate}
            clearable
          />
        </Group>

        {hasActiveFilters && (
          <Group justify="space-between">
            <Group gap="xs" wrap="wrap">
              {values.channel && (
                <Badge variant="light">{values.channel}</Badge>
              )}
              {values.status && (
                <Badge variant="light" color="grape">
                  {values.status}
                </Badge>
              )}
              {values.routingDecision && (
                <Badge variant="light" color="orange">
                  {values.routingDecision}
                </Badge>
              )}
              {values.communicationType && (
                <Badge variant="light" color="teal">
                  {values.communicationType}
                </Badge>
              )}
              {values.recipient && (
                <Badge variant="light" color="orange">
                  Intended recipient
                </Badge>
              )}
              {values.caseNumber && (
                <Badge variant="light" color="indigo">
                  Case #
                </Badge>
              )}
              {values.dateRange[0] && (
                <Badge variant="light" color="cyan">
                  Date range
                </Badge>
              )}
            </Group>
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={onClear}
            >
              Clear all
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
